import { isString, logger, useOnlineStatus } from '@tunasong/models'
import { features, OAuth, profilesApi, useSelector, useThunkDispatch } from '@tunasong/redux'
import { FullPageProgress } from '@tunasong/ui-lib'
import queryString from 'query-string'
import React, { useEffect, useEffectEvent, useState } from 'react'
import NotAuthorizedPage from '../pages/401.js'
import { TARGET_PATH } from './target-path.js'

const AuthGuard: React.FC<{ children: React.ReactNode }> = props => {
  const { children } = props
  const isLoggedIn = useSelector(state => state.user.isLoggedIn)
  const dispatch = useThunkDispatch()

  /** Authorize with the IDP */
  const auth = async () => {
    localStorage.setItem(TARGET_PATH, document.location.pathname)
    /** Mode signup? */
    const params = queryString.parse(document.location.search)
    const signup = isString(params.mode) && params.mode === 'signup'
    return OAuth.authorize({ signup })
  }

  /** Check if logged in. If not, redirect to federated login screen by calling authorize() */
  const [isAuthorizing, setIsAuthorizing] = useState(true)

  const online = useOnlineStatus()

  const handleLoginStateChanged = ({ isLoggedIn, online }: { isLoggedIn: boolean; online: boolean }) => {
    if (isLoggedIn) {
      return
    }

    setIsAuthorizing(true)

    /** Check if we have any tokens in localStorage first */
    const hasStoredSession = OAuth.hasSession()

    /** Do not try to refresh if we're offline, but also don't redirect to IDP if we have stored tokens */
    const shouldRefresh = Boolean(online && hasStoredSession)

    OAuth.getSession(shouldRefresh)
      .then(session => {
        if (session?.idToken?.payload?.sub) {
          dispatch(features.user.actions.setAuth(session))
          // RTK Query port
          dispatch(
            profilesApi.util.prefetch(
              'loadProfile',
              {
                emailOrUserId: session.idToken.payload.sub,
              },
              {}
            )
          )
          return
        }
        if (!online) {
          /** We have tokens but are offline - wait for connectivity instead of redirecting */
          logger.warn('Offline - waiting for connectivity to complete auth')
          return
        }
        /** Redirect to IDP if there are no stored tokens at all */
        auth().catch(e => logger.error('auth failed', e))
        return
      })
      .catch(() => {
        if (!online) {
          /** We have tokens but are offline - wait for connectivity */
          logger.warn('Session refresh failed while offline - waiting for connectivity')
          return
        }

        /** If getSession fails and we have no stored session, redirect to IDP */
        OAuth.logout()
        dispatch(features.user.actions.signout())
        auth().catch(e => logger.error('auth failed', e))
      })
      .finally(() => setIsAuthorizing(false))
  }

  const loginStateChangedEvent = useEffectEvent(handleLoginStateChanged)

  useEffect(() => {
    logger.debug('AuthGuard: login state or online state changed', { isLoggedIn, online })
    loginStateChangedEvent({ isLoggedIn, online })
  }, [isLoggedIn, online])

  if (isLoggedIn) {
    return <>{children}</>
  }

  return isAuthorizing ? <FullPageProgress /> : <NotAuthorizedPage />
}

export default AuthGuard
